import React, { useEffect, useState } from 'react';
import { Id, Review, ReSession } from '../../interfaces/types';
import { Box, Divider, IconButton, ListItem, ListSubheader, Typography } from '@mui/material';
import { useUserContext } from '../../providers/userProvider';
import { useCustomThemeContext } from '../../providers/customThemeProvider';
import CreateCommentForm from '../comments/CreateCommentForm';
import CommentList from '../comments/CommentList';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import DividerHorizontal from '../dividers/DividerHorizontal';
import { useEventContext } from '../../providers/EventProvider';
import { usePermissionContext } from '../../providers/PermissionProvider';
import ReviewForm from './ReviewForm';
import { useSessionContext } from '../../providers/SessionProvider';
import { Cancel, Edit } from '@mui/icons-material';

interface ReviewListItemProps {
    // Define your props here
    review: Review;
}

const ReviewListItem: React.FC<ReviewListItemProps> = ({ review }) => {
    const { theme } = useCustomThemeContext();
    const { getUserById } = useUserContext();
    const { getEventBySessionId, isXpFactorActive } = useEventContext();
    const { getSessionById } = useSessionContext();
    const { hasCommentCreatePermission, hasReviewEditPermission } = usePermissionContext();
    const usernameReviewer = getUserById(review.createdBy)?.username || 'Unknown User';

    const [currentEvent, setCurrentEvent] = useState(getEventBySessionId(review.sessionId));
    const [isEditable, setIsEditable] = useState(false);

    useEffect(() => {
        const event = getEventBySessionId(review.sessionId);
        setCurrentEvent(event);
    }, [review.sessionId]);

    return (
        <Box width={'100%'} border={1} borderRadius={1} borderColor={theme.palette.primary.main}>
            {currentEvent?.eventConfiguration.isReviewCreationActive && (<>
            {hasReviewEditPermission(currentEvent?.id as Id, review) && isEditable && <Box display="flex" justifyContent="flex-end">
                <IconButton
                    color="primary"
                    aria-label={isEditable ? 'Cancel' : 'Edit'}
                    onClick={() => setIsEditable(!isEditable)}>
                    <Cancel />
                </IconButton>
            </Box>}
            {hasReviewEditPermission(currentEvent?.id as Id, review) && !isEditable &&
                <Box position="absolute" top={65} right={10} sx={{ alignSelf: 'flex-end' }}>
                    <IconButton
                        color="primary"
                        aria-label={isEditable ? 'Cancel' : 'Edit'}
                        onClick={() => setIsEditable(!isEditable)}>
                        <Edit />
                    </IconButton>
                </Box>}
                </>)}
            {isEditable ? <ReviewForm editForm session={getSessionById(review.sessionId) as ReSession} review={review} onClose={() => setIsEditable(!isEditable)} /> :
                <Box p={2}>
                    <Typography sx={{ color: theme.palette.primary.main }}>What {usernameReviewer} liked:</Typography>
                    <Typography sx={{ color: 'black' }}>{review.whatIlike}</Typography>
                    <Typography sx={{ color: theme.palette.primary.main }}>What {usernameReviewer} wants to see improved:</Typography>
                    <Typography sx={{ color: 'black' }}>{review.improvementOpportunities}</Typography>
                    {currentEvent && isXpFactorActive(currentEvent) && <Box display="flex" justifyContent="space-between">
                        <Typography sx={{ color: theme.palette.primary.main }}>
                            XP-factor:
                        </Typography>
                        <Typography sx={{ color: 'black' }}>
                            {review.xpFactor}<span style={{ color: theme.palette.primary.main }}>/10</span>
                        </Typography>
                    </Box>}
                    <Box display="flex" justifyContent="space-between">
                        <Typography sx={{ color: theme.palette.primary.main }}>
                            Score:
                        </Typography>
                        <Typography sx={{ color: 'black' }}>
                            {review.score}<span style={{ color: theme.palette.primary.main }}>/10</span>
                        </Typography>
                    </Box>
                    <DividerHorizontal />
                    <CommentList review={review} />

                    {currentEvent && currentEvent.eventConfiguration.isCommentCreationActive && hasCommentCreatePermission(currentEvent.id) &&
                        <CreateCommentForm review={review} />
                    }
                </Box>
            }
        </Box>
    );
};

export default ReviewListItem;